import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Col, Divider, Form, Input, Modal, Row, Space } from "antd";

import DatePicker from "../../Common/DatePicker";
import Notify from "../../../utils/notification-handler";
import CommonService from "../../../services/CommonService";
import DefaultDataHandler from "../../../utils/default-data-handler";
import { IFileData } from "../../../types/FileData.types";

import { TRANSLATION_KEY } from "../../../constants/commonConstants";
import { FILE_DATA_DATATABLE_SETTINGS } from "../../../constants/fileDataConstants";
import { DATA_TABLE_HIDE_UPDATE_MODAL, DATA_TABLE_RELOAD, DATA_TABLE_SHOW_UPDATE_MODAL } from "../../../constants/pubSubKeys";



export interface IFileDataUpdateModalProps {
}

export const FileDataUpdateModal: React.FC<IFileDataUpdateModalProps> = () => {

    const { t } = useTranslation([TRANSLATION_KEY.toString()]);
    const [form] = Form.useForm<IFileData>();
    const [isShowModal, setIsShowModal] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [updatingFormModel, setUpdatingFormModel] = useState<IFileData | null>(null);


    const closeUpdateModal = () => {
        form.resetFields();
        setUpdatingFormModel(null);
        setIsShowModal(false);
    }


    useEffect(() => {
        let showModalPubSub = PubSub.subscribe(DATA_TABLE_SHOW_UPDATE_MODAL, async (msg: string, updatingItem: IFileData | null) => {

            if (updatingItem === null) {
                updatingItem = DefaultDataHandler.newFileData();
            }
            else {
                if (updatingItem.uploaded) { updatingItem.uploaded = new Date(updatingItem.uploaded); }
                if (updatingItem.lastEdited) { updatingItem.lastEdited = new Date(updatingItem.lastEdited); }
            }

            form.setFieldsValue(updatingItem);
            setUpdatingFormModel(updatingItem);
            setIsShowModal(true);

        });

        let hideModalPubSub = PubSub.subscribe(DATA_TABLE_HIDE_UPDATE_MODAL, () => {
            closeUpdateModal();
        })

        return () => {
            PubSub.unsubscribe(showModalPubSub);
            PubSub.unsubscribe(hideModalPubSub);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const onFinish = (updatingItem: IFileData) => {
        if (!updatingFormModel) {
            return;
        }
        updatingItem.id = updatingFormModel.id;
        edit(updatingItem);
    };


    const edit = async (updatingItem: IFileData) => {
        setIsLoading(true);

        let url = `data/${FILE_DATA_DATATABLE_SETTINGS.dataEndpoint}/${updatingItem.id}`
        const odataResponse = await CommonService.patch(url, updatingItem);

        if (odataResponse === 0 || odataResponse === 1) {
            Notify.success(t('common-itemUpdatedSuccessfully'));
        } else {
            Notify.error(t('common-itemUpdatingFailed'));
        }

        PubSub.publish(DATA_TABLE_RELOAD);
        closeUpdateModal();
        setIsLoading(false);
    }

    return (
        <>
            {
                updatingFormModel &&
                <Modal
                    title={(updatingFormModel.id !== 0) ? t('resources-fileData-fileDataEditModalTitle') : t('resources-fileData-fileDataAddModalTitle')}
                    className="data-table-item-update-modal"
                    visible={isShowModal}
                    onCancel={closeUpdateModal} footer={null}>

                    <Form form={form}
                        name="data-table-item-update-form"
                        layout="vertical"
                        onFinish={onFinish}>

                        <Row gutter={24}>
                            <Col span={24}>
                                <Form.Item
                                    name="title"
                                    label={t('fileData-title')}
                                    required={true}
                                    rules={[{ required: true, message: `${t('common-validation-requiredValidationError')}` }]}>
                                    <Input />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item
                                    name="fileName"
                                    label={t('fileData-fileName')}>
                                    <Input disabled={true} />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item
                                    name="path"
                                    label={t('fileData-path')}>
                                    <Input disabled={true} />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item
                                    name="uploaded"
                                    label={t('fileData-created')}>
                                    <DatePicker className="w-100" disabled={true} />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item
                                    name="lastEdited"
                                    label={t('fileData-updated')}>
                                    <DatePicker className="w-100" disabled={true} />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item
                                    name="fileHash"
                                    label={t('fileData-hash')}>
                                    <Input disabled={true} />
                                </Form.Item>
                            </Col>

                        </Row>

                        <Divider />

                        <Row justify="end">
                            <Col span={24}>
                                <Space className="data-table-item-update-modal-footer">
                                    <Button onClick={closeUpdateModal}>
                                        {t('common-cancel')}
                                    </Button>
                                    <Button type="primary" htmlType="submit" loading={isLoading}>
                                        {t('common-save')}
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Form>

                </Modal>
            }
        </>
    )
}